import { Check, Loader2, Plus } from "lucide-react";
import type { ProjectSelection, Server } from "@/lib/api";
import { recommendedRows } from "@/lib/recommendedIntegrations";
import { Button } from "@/components/ui/button";
import { IntegrationLogo } from "@/components/IntegrationLogo";

// IntegrationsPanel shows the integrations the drafted playbook needs and lets
// the user attach (connected-but-not-on-this-agent) or open the Add-integration
// modal (to pick an existing server or connect a new one). State mirrors
// deploy.SelectServer (selection==null ⇒ all connected are attached).
export function IntegrationsPanel({
  needed,
  servers,
  selection,
  attaching,
  onAttach,
  onAdd,
}: {
  needed: string[];
  servers: Server[];
  selection: ProjectSelection | null;
  attaching: string | null;
  onAttach: (serverName: string) => void;
  onAdd: () => void;
}) {
  const rows = recommendedRows(needed, servers, selection);
  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border bg-background p-3">
      <div className="mb-2 text-xs font-medium text-muted-foreground">Integrations this agent needs</div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-2.5">
            <IntegrationLogo name={r.key} />
            <span className="min-w-0 flex-1 truncate text-sm">{r.display}</span>
            {r.kind === "attached" ? (
              <span className="inline-flex shrink-0 items-center gap-1 text-xs text-success">
                <Check className="h-3.5 w-3.5" /> Added
              </span>
            ) : r.kind === "attach" ? (
              <Button size="sm" variant="outline" className="h-7 shrink-0" disabled={attaching !== null} onClick={() => onAttach(r.serverName)}>
                {attaching === r.serverName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Attach
              </Button>
            ) : (
              <Button size="sm" className="h-7 shrink-0" onClick={onAdd}>
                Connect
              </Button>
            )}
          </div>
        ))}
      </div>
      <button onClick={onAdd} className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <Plus className="h-3.5 w-3.5" /> Add an integration
      </button>
    </div>
  );
}
